/**
 * Pure helpers for the `/pr` + `/inbox` pending-approvals panel.
 *
 * Everything here is a decision the panel must get right BEFORE it dispatches
 * anything, isolated from React so it can be tested — `apps/dashboard/web/**` is
 * ESLint-ignored and coverage-excluded, so a unit test is the only mechanical
 * guard these rules get.
 */

/** A staged proposal as `list_pa_pending_actions` returns it on the wire. */
export interface PendingApprovalRow {
  readonly id: string;
  readonly operator_ref: string;
  readonly action: string;
  readonly action_input: Record<string, unknown> | null;
  readonly summary: string | null;
  readonly reason: string | null;
  readonly status: string;
  readonly expires_at: string;
  readonly created_at: string;
  readonly confirmation_token: string | null;
}

/** The two lanes this panel serves. `operator_ref` is required by the action. */
export type ApprovalLane = "peter" | "inbox";

/**
 * The list input.
 *
 * Two things here are load-bearing and neither is obvious from the call site:
 *
 *  - **`include_confirmation_token: true`.** It defaults to false and the field
 *    comes back `null` — so without it every Approve would 404 at
 *    `resolve_pa_action`, which matches by token.
 *  - **`live_only` is NOT passed.** Expired rows still render, greyed, with a
 *    "window closed" chip. Filtering them out would make a proposal vanish from
 *    the panel with no explanation, and "it disappeared" is indistinguishable
 *    from "it was sent".
 *
 * There is deliberately no `request_id` anywhere near this. See
 * {@link PENDING_LIST_MUST_NOT_PIN_THE_WIRE}.
 */
export function buildPendingListInput(
  lane: ApprovalLane,
  limit = 50,
): Record<string, unknown> {
  return {
    operator_ref: lane,
    status: "pending",
    include_confirmation_token: true,
    limit,
  };
}

/**
 * Why this panel must NEVER pass an explicit `requestId` to `dispatchAction`.
 *
 * `list_pa_pending_actions` declares `responsePiiPaths` over `summary`,
 * `action_input`, `reason` **and `confirmation_token`**. On an idempotency
 * replay the chokepoint nulls all four before returning the stored response
 * (invariant 11). A pinned wire would therefore give exactly ONE good read and
 * then an unbounded series of blank cards with dead Approve buttons — a bug that
 * looks like a rendering fault and is actually working as designed.
 *
 * `dispatchAction` mints a fresh uuid-v7 per call, so the DEFAULT path is
 * correct; the hazard is someone later "optimising" the refetch by pinning it.
 * The same trap on `resolve_pa_action` is worse: `deriveIdempotencyKey` hashes
 * the WIRE and not the input, so a stable `pa:<rowId>` would make
 * Approve-then-Reject on one row short-circuit to the cached APPROVE response
 * with `ok: true` — the UI would say rejected and the drain would send it.
 * Double-submit safety is a disabled button plus a refetch, never a stable wire.
 */
export const PENDING_LIST_MUST_NOT_PIN_THE_WIRE = true;

/**
 * The resolve input.
 *
 * **`operator_ref` is always passed**, even though it is optional.
 * `resolve_pa_action` matches by the globally-unique `confirmation_token` when
 * it is omitted — safe when a token only ever reached one operator's private
 * Telegram chat, and no longer safe now that a studio-scoped staff JWT can see
 * both lanes. Passing the ref makes the row-match assert the isolation instead
 * of assuming it.
 */
export function buildResolveInput(
  lane: ApprovalLane,
  confirmationToken: string,
  decision: "approve" | "reject",
): Record<string, unknown> {
  return {
    operator_ref: lane,
    confirmation_token: confirmationToken,
    decision,
  };
}

/**
 * Pull the matter id out of a proposal summary.
 *
 * The inbox agent carries `inbox:<uuid>` in the SUMMARY (never in
 * `action_input`) — the same token `inbox-drain.sh` parses for its pre-send
 * re-check, and the same one `propose_pa_action` keys `dedupe_key` on. A reject
 * without it cannot be followed by `record_inbox_feedback`, which is why the
 * panel REFUSES to reject such a row rather than doing half the job.
 */
export function parseInboxThreadRef(summary: string | null): string | null {
  if (summary === null) return null;
  const m = /inbox:([0-9a-fA-F-]{36})/.exec(summary);
  return m?.[1] ?? null;
}

/** The PR twin — `pr_engagement_id=<uuid>`, as `peter-drain.sh` parses it. */
export function parsePrEngagementRef(summary: string | null): string | null {
  if (summary === null) return null;
  const m = /pr_engagement_id=([0-9a-fA-F-]{36})/.exec(summary);
  return m?.[1] ?? null;
}

/**
 * A short, human preview of what will actually be sent.
 *
 * Reads the recognised body fields of the send actions this queue stages. An
 * action whose body we do not recognise returns `null` and the panel shows the
 * summary alone — **never a guess**. Approving something the panel could not
 * read is a decision the operator should make on the Telegram card, where the
 * agent wrote the description itself.
 */
export function draftPreview(
  action: string,
  input: Record<string, unknown> | null,
): { readonly to: string | null; readonly subject: string | null; readonly body: string } | null {
  if (input === null) return null;
  const str = (k: string): string | null =>
    typeof input[k] === "string" ? (input[k] as string) : null;
  switch (action) {
    case "send_gmail":
    case "send_email": {
      const body = str("body") ?? str("html") ?? str("text");
      if (body === null) return null;
      return { to: str("to"), subject: str("subject"), body };
    }
    case "send_whatsapp_message":
    case "send_telegram_message":
    case "send_instagram_dm": {
      const body = str("message") ?? str("text") ?? str("body");
      if (body === null) return null;
      return { to: str("to") ?? str("chat_id") ?? str("recipient_id"), subject: null, body };
    }
    default:
      return null;
  }
}

/** How the drain's heartbeat renders beside the Approve button. */
export interface ApprovalLiveness {
  /** `false` disables Approve — see {@link approvalLiveness}. */
  readonly canApprove: boolean;
  /** One short sentence, always shown. */
  readonly label: string;
  readonly tone: "ok" | "warn" | "bad";
}

/** The drain-heartbeat shape the board endpoints return. */
export interface DrainLivenessWire {
  readonly ok: boolean | null;
  readonly tested_at: string | null;
  readonly paused: boolean;
  readonly stale: boolean;
}

/**
 * Turn the drain heartbeat into Approve-button copy.
 *
 * The rule is deliberately not "green card ⇒ enabled". The capability's own
 * window is 26h (the laptop sleeps; a card red every morning is a card nobody
 * reads), so a drain dead for twenty hours still reads healthy on `/status` —
 * stated in `status-page.md` rather than tuned away. A button that SENDS needs a
 * sharper question than "is the lane broken?", so this shows the beat AGE in
 * every state and disables only on the unambiguous ones:
 *
 *  - **paused** — the sentinel is deliberately set; an approval would queue
 *    indefinitely, and nothing expires an APPROVED row (the daily sweep only
 *    touches `pending`). Disabled.
 *  - **stale** past the capability window, or **never beat** — we cannot say the
 *    drain is running at all. Disabled.
 *  - **last beat reported a failure** — enabled, but flagged: the drain is alive
 *    and a terminal send failure is exactly the state a human should look at.
 *
 * Everything else is enabled with the age shown, so "approved at 23:50 and the
 * laptop is shut" is a judgement Mario makes with the fact in front of him.
 */
export function approvalLiveness(
  drain: DrainLivenessWire | null,
  now: number,
): ApprovalLiveness {
  if (drain === null || drain.tested_at === null) {
    return {
      canApprove: false,
      label: "The drain has never reported — nothing would execute an approval.",
      tone: "bad",
    };
  }
  const age = now - new Date(drain.tested_at).getTime();
  const ago = humanAge(age);
  if (drain.paused) {
    return {
      canApprove: false,
      label: `Drain PAUSED (last beat ${ago}) — an approval would sit queued until the sentinel is cleared, and nothing expires an approved row.`,
      tone: "warn",
    };
  }
  if (drain.stale) {
    return {
      canApprove: false,
      label: `Drain last reported ${ago} — too long to assume it is running.`,
      tone: "bad",
    };
  }
  if (drain.ok === false) {
    return {
      canApprove: true,
      label: `Drain last beat ${ago} and reported a FAILURE — worth checking before you approve more.`,
      tone: "warn",
    };
  }
  return {
    canApprove: true,
    label: `Drain last beat ${ago} — approvals go out on its next beat.`,
    tone: "ok",
  };
}

/** Compact relative age. Negative (clock skew) reads as "just now". */
export function humanAge(ms: number): string {
  if (ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** True when the row's approval window has already lapsed. */
export function isExpired(row: PendingApprovalRow, now: number): boolean {
  return new Date(row.expires_at).getTime() <= now;
}

/**
 * Why an inbox REJECT is refused outright rather than done partially.
 *
 * A bare `resolve_pa_action{reject}` leaves the matter at `awaiting_mario` with
 * `next_action_at` untouched — the worker's `due_before` sweep never re-picks
 * it, so it is silent until `reconcile_inbox_board`'s 48h drift report, which
 * reports and repairs nothing. The feedback call is what bumps `revision_round`
 * and re-arms the cursor, and it needs the thread id from the summary. No
 * `inbox:<uuid>` token, no safe reject from here — point at the Telegram card,
 * where the reply-to-card path binds by `button_message_id` instead.
 *
 * Peter's reject is legitimately terminal: his lane has no feedback loop.
 */
export function inboxRejectBlockedReason(row: PendingApprovalRow): string | null {
  if (parseInboxThreadRef(row.summary) !== null) return null;
  return "This proposal's summary carries no inbox:<id>, so rejecting here would leave the matter stalled at awaiting_mario with nothing to re-sweep it. Reject it on the Telegram card instead.";
}

/**
 * Whether a feedback control may be offered for this row.
 *
 * NEVER for an already-`approved` row. `record_inbox_feedback` does not look at
 * proposal status, and the drain's pre-send re-check passes `revising` — so
 * feedback on an approved row ships the original AND has the agent stage a
 * revision. The correspondent gets two replies.
 */
export function mayRecordFeedback(row: PendingApprovalRow): boolean {
  return row.status === "pending";
}
